import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import axios from 'axios';
import selectWebsocket from '../../../../Websocket/selectors';
import { AGGREGATED_MACHINE_STAT_ENDPOINT, DEFAULT_LIVE_WINDOW_MINUTES, DEFAULT_REFRESH_INTERVAL_MS, RAW_MACHINE_STAT_ENDPOINT } from './machineStatDataConstants';
import {
  applyCalculatedExpectedSpeed,
  buildDisplayDataByArea,
  buildEmptyData,
  buildSummaryByArea,
  hasExistingDatasetData,
  mergeLivePoints,
  normalizeGroupedResponse,
  normalizePoint,
  replaceLiveDataByArea
} from './machineStatDataHelpers';
import { getAllStatisticsAreaIdentifiers } from './statisticsDatasetHelpers';
import useAverageLengthStatistics from './useAverageLengthStatistics';

export default function useMachineStatData({ datasets, rangeMode, from, to, liveWindowMinutes = DEFAULT_LIVE_WINDOW_MINUTES, refreshIntervalMs = DEFAULT_REFRESH_INTERVAL_MS }) {
  const [sourceDataByArea, setSourceDataByArea] = useState(() => buildEmptyData(datasets));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [lastUpdated, setLastUpdated] = useState('');
  const sourceDataByAreaRef = useRef(sourceDataByArea);
  const websocketObject = useSelector((state) => selectWebsocket()(state)?.websocket?.websocketObject);
  const calculatedDataset = datasets.find((dataset) => dataset.calculateExpectedSpeed);
  const { averageLength, expectedSpeedAverage, lengthEntries } = useAverageLengthStatistics({
    dataset: calculatedDataset,
    rangeMode,
    from,
    to,
    liveWindowMinutes,
    refreshIntervalMs
  });
  const averageLengths = useMemo(() => (calculatedDataset ? { [calculatedDataset.averageLengthIdentifier]: averageLength } : {}), [averageLength, calculatedDataset]);
  const dataByArea = useMemo(
    () => applyCalculatedExpectedSpeed(buildDisplayDataByArea(sourceDataByArea, datasets, rangeMode), calculatedDataset, expectedSpeedAverage, lengthEntries),
    [calculatedDataset, datasets, expectedSpeedAverage, lengthEntries, rangeMode, sourceDataByArea]
  );
  const summaryByArea = useMemo(() => buildSummaryByArea(dataByArea, datasets), [dataByArea, datasets]);

  const datasetAreas = useMemo(() => getAllStatisticsAreaIdentifiers(datasets), [datasets]);
  const requestKey = useMemo(() => (rangeMode === 'live' ? `live:${liveWindowMinutes}` : `range:${from ?? ''}|${to ?? ''}`), [from, liveWindowMinutes, rangeMode, to]);

  useEffect(() => {
    sourceDataByAreaRef.current = sourceDataByArea;
  }, [sourceDataByArea]);

  useEffect(() => {
    setSourceDataByArea(buildEmptyData(datasets));
    setLastUpdated('');
    setError('');
  }, [datasetAreas.join('|'), requestKey]);

  const fetchData = useCallback(async () => {
    if (!datasets?.length) {
      setSourceDataByArea({});
      setError('');
      return;
    }

    if (rangeMode === 'range' && (!from || !to)) {
      setSourceDataByArea(buildEmptyData(datasets));
      setError('');
      return;
    }

    const showLoadingState = rangeMode !== 'live' || !hasExistingDatasetData(sourceDataByAreaRef.current, datasets);

    setLoading(showLoadingState);
    setError('');

    const endpoint = rangeMode === 'range' ? AGGREGATED_MACHINE_STAT_ENDPOINT : RAW_MACHINE_STAT_ENDPOINT;
    const params = rangeMode === 'range' ? { from, to } : { window_minutes: liveWindowMinutes };

    try {
      const response = await axios.get(endpoint, { params });
      const normalizedData = normalizeGroupedResponse(response?.data, datasets);

      setSourceDataByArea((previousData) => {
        if (rangeMode !== 'live') {
          return normalizedData;
        }

        return replaceLiveDataByArea(previousData, normalizedData, datasets, liveWindowMinutes);
      });

      setLastUpdated(new Date().toISOString());
    } catch (fetchError) {
      if (rangeMode !== 'live') {
        setSourceDataByArea(buildEmptyData(datasets));
      }

      setError(fetchError?.message ?? 'Failed to load statistics data.');
    } finally {
      setLoading(false);
    }
  }, [datasets, from, liveWindowMinutes, rangeMode, to]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (rangeMode !== 'live' || !datasets?.length) return undefined;

    const timerId = window.setInterval(() => {
      fetchData();
    }, refreshIntervalMs);

    return () => window.clearInterval(timerId);
  }, [datasets, fetchData, rangeMode, refreshIntervalMs]);

  useEffect(() => {
    if (!websocketObject || rangeMode !== 'live' || !datasets?.length) return undefined;

    const allowedAreas = new Set(getAllStatisticsAreaIdentifiers(datasets));

    const handler = (event) => {
      try {
        const msg = JSON.parse(event.data);
        if (msg.type !== 'flow-stats-data') return;

        const payload = msg.payload ?? {};
        if (!String(payload.identifier ?? '').endsWith('_STAT')) return;

        const rawEntry = {
          ...(payload.data ?? {}),
          timestamp: payload.timestamp,
          areaIdentifier: payload.data?.areaIdentifier ?? null
        };

        const areaIdentifier = rawEntry.areaIdentifier;
        if (!areaIdentifier || !allowedAreas.has(areaIdentifier)) return;

        const normalizedPoint = normalizePoint(rawEntry);
        if (!normalizedPoint.date) return;

        setSourceDataByArea((previousData) => ({
          ...previousData,
          [areaIdentifier]: mergeLivePoints(previousData?.[areaIdentifier] ?? [], [normalizedPoint], liveWindowMinutes)
        }));

        setLastUpdated(new Date().toISOString());
      } catch {
        /* ignore non-JSON */
      }
    };

    websocketObject.addEventListener('message', handler);

    return () => {
      websocketObject.removeEventListener('message', handler);
    };
  }, [datasets, liveWindowMinutes, rangeMode, websocketObject]);

  return { dataByArea, chartDataByArea: dataByArea, summaryByArea, averageLengths, loading, error, lastUpdated };
}
